import React from 'react';
import type { SelectProps } from 'antd';

type CustomTagProps = Parameters<NonNullable<SelectProps['tagRender']>>[0];

/** Class styled in ChartFilterBar — ellipsis only when one value shown in collapsed multi-select. */
export const CHART_FILTER_SINGLE_SELECTED_VALUE_CLASS = 'asper-chart-filter-single-selected-value';

const preventTagMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
};

const hiddenTag = <span style={{ display: 'none' }} aria-hidden />;

/** Plain text cell for ant Select — no Tag/pill chrome. */
function SelectionText({
    children,
    title,
}: {
    children: React.ReactNode;
    title?: string;
}) {
    return (
        <span
            onMouseDown={preventTagMouseDown}
            className="inline-block max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap align-middle text-[14px] leading-[22px] text-[#2f2e38]"
            title={title}
        >
            {children}
        </span>
    );
}

export type ChartMultiTagOpts =
    | {
          /** Z-axis: show “All” on the first enabled value only */
          kind: 'zAll';
          firstTagValue: string;
      }
    | undefined;

/**
 * Collapses multi-select UI to one line: one value → truncated label; 2+ → “n selected”.
 * Optionally supports Z-axis “All” when every enabled option is selected.
 */
export function chartMultiSelectTagRender(
    selected: string[],
    getLabelForValue: (v: string) => string,
    opts?: ChartMultiTagOpts,
): SelectProps['tagRender'] {
    return (props: CustomTagProps) => {
        const value = props.value as string;

        if (opts?.kind === 'zAll') {
            if (value !== opts.firstTagValue) return hiddenTag;
            return <SelectionText title="All">All</SelectionText>;
        }

        const firstIdx = selected.indexOf(value);
        if (firstIdx !== 0) return hiddenTag;

        const n = selected.length;
        if (n === 0) return <></>;
        if (n === 1) {
            const label = getLabelForValue(selected[0]);
            return (
                <span
                    onMouseDown={preventTagMouseDown}
                    className={`${CHART_FILTER_SINGLE_SELECTED_VALUE_CLASS} text-[14px] leading-[22px] text-[#2f2e38]`}
                    title={label}
                >
                    {label}
                </span>
            );
        }
        const summary = `${n} selected`;
        return <SelectionText title={summary}>{summary}</SelectionText>;
    };
}
